import { Component, Input, computed, signal } from '@angular/core';
import { ImagesWidget, ImageItem, Theme, ImagesLayout } from '../../models/reader-document';
import { NgTemplateOutlet } from '@angular/common';
import { createCarouselController } from './images-carousel.logic';

type DarkTheme = Theme & { isDark?: boolean };

@Component({
  selector: 'wr-images-widget',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './images.component.html'
})
export class ImagesWidgetComponent {
  private widgetSig = signal<ImagesWidget | null>(null);
  private themeSig = signal<DarkTheme | null>(null);

  private carousel = createCarouselController<ImageItem>({
    items: () => this.images(),
    isInteractiveTarget: (t) => !!(t as HTMLElement)?.closest?.('button,a'),
    swipeThreshold: 35,
    lockAngleRatio: 1.2,
    transitionMs: 220,
  });

  prevImage = this.carousel.prevItem;
  nextImage = this.carousel.nextItem;

  trackTransform = this.carousel.trackTransform;
  trackTransition = this.carousel.trackTransition;

  onTrackTransitionEnd = (e: TransitionEvent) => this.carousel.onTrackTransitionEnd(e);

  @Input() set json(value: ImagesWidget) {
    this.widgetSig.set(value);
    this.carousel.indexSig.set(0);
  }


  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set((value as DarkTheme) ?? null);
  }

  widget = computed(() => this.widgetSig());
  images = computed(() => this.widget()?.images ?? []);
  activeIndex = this.carousel.activeIndex;
  activeImage = this.carousel.activeItem;

  layout = computed<ImagesLayout | 'horizontal' | undefined>(() => {
    const w = this.widget();
    return (w as any)?.layout ?? undefined;
  });

  isCarousel = computed(() => {
    const w = this.widget();
    if (!w) return false;
    return this.layout() === 'horizontal' || w.type === 'imageSlider' || w.type === 'imagesHorizontal';
  });

  // ---- theme helpers
  private colors = computed(() => {
    const theme = this.themeSig();
    if (!theme) return null;
    return theme.isDark ? theme.darkColors : theme.lightColors;
  });

  background = computed(() => this.widget()?.style?.['background'] ?? 'transparent');
  cornerRadius = computed(() => this.themeSig()?.cornerRadius ?? 0);

  titleColor = computed(() => this.widget()?.style?.['titleColor'] ?? this.colors()?.title ?? this.colors()?.primary ?? '#111827');
  subtitleColor = computed(() => this.widget()?.style?.['subtitleColor'] ?? this.colors()?.subtitle ?? this.colors()?.secondary ?? '#4b5563');

  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');

  dotActive = computed(() => this.colors()?.accent ?? '#ff5e38');
  dotInactive = computed(() => (this.themeSig()?.isDark ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)'));

  // ---- helpers for grid sizing
  aspect(fill?: boolean, compact?: boolean, mode?: 'grid' | 'carousel') {

    if (mode === 'carousel') return '1 / 1';
    if (fill) return '1 / 1';
    if (compact) return '16 / 9';
    return '1 / 1';
  }

  imageAspect = (im: ImageItem | null | undefined) => {
    const w = im?.size?.[0];
    const h = im?.size?.[1];
    if (!w || !h || w <= 0 || h <= 0) return '1 / 1';
    return `${w} / ${h}`;
  };


  firstN(n: number): ImageItem[] {
    return (this.images() ?? []).slice(0, n);
  }

  imgSrc(im: ImageItem): string {
    return im.url || im.fallbackUrl || '';
  }

  safeLink(im: ImageItem): string | null {
    const link = im.linkURL?.trim();
    if (!link) return null;
    return link;
  }

  altText(im: ImageItem): string {
    return im.altTitle || im.title || im.id || '';
  }

  onImgError(event: Event, im: ImageItem) {
    const el = event.target as HTMLImageElement;
    if (im.fallbackUrl && el.src !== im.fallbackUrl) {
      el.src = im.fallbackUrl;
      return;
    }
    el.style.display = 'none';
  }

  // CAROUSEL

  onPointerDown = (e: PointerEvent) => this.carousel.onPointerDown(e);
  onPointerMove = (e: PointerEvent) => this.carousel.onPointerMove(e);
  onPointerUp = (e: PointerEvent) => this.carousel.onPointerUp(e);
  prev = () => this.carousel.prev();
  next = () => this.carousel.next();
  goTo = (i: number) => this.carousel.goTo(i);

  carouselAspect = computed(() => {
    const first = this.images()?.[0];
    const fallback = '16 / 9';
    const w = first?.size?.[0];
    const h = first?.size?.[1];
    if (!w || !h || w <= 0 || h <= 0) return fallback;

    return `${w} / ${h}`;
  });

}